import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, inject, isDevMode } from '@angular/core';
import { provideRouter, Router, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';

import { routes } from './app.routes';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { TranslocoHttpLoader } from './config/transloco-loader';
import { provideTransloco } from '@ngneat/transloco';
import { provideAnimations } from '@angular/platform-browser/animations';
import { globalRequestHeadersInterceptor } from './core/interceptors/global-request-headers.interceptor';
import { csrfErrorResponseInterceptor } from './core/interceptors/csrf-interceptor';
import { noStoreErrorResponseInterceptor } from './core/interceptors/no-store-error-response';
import { StoreIdService } from './core/services/store-id.service';
import { APP_BASE_HREF } from '@angular/common';
import { provideLottieOptions } from 'ngx-lottie';
import * as Sentry from "@sentry/angular";
import player from 'lottie-web';
import { ThemeService } from './core/services/theme.service';
import { NeoErrorHandler } from './core/utils/error-handler';
import { xsrfInterceptor } from './core/interceptors/xsrf.interceptor';

/**
 * Anything that we need to configure before the application is bootstraped should be executed here
 * For example we added the StoreIdService initialization here, so that the application has this available
 * before any component is rendered or any http request is performed by the client
*/

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: ErrorHandler,
      useClass: NeoErrorHandler,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_BASE_HREF,
      // WARNING: This must match the param passed in in the production build script: `"build:prod": "ng build --base-href /test/"`
      useValue: '/ordering'
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        // This factory's sole purpose is to initialize the StoreIdService instance. No return value required.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const _service = inject(StoreIdService);
        inject(ThemeService).loadTheme()
        // eslint-disable-next-line no-empty-function
        return () => {};
      },
      deps: [Sentry.TraceService],
      multi: true,
    },
    provideRouter(
      routes,
      withComponentInputBinding(), // see: https://netbasal.com/binding-router-information-to-routed-component-inputs-in-angular-78ee92f63e64
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'disabled' }),
    ),
    provideHttpClient(
      withInterceptors([
        globalRequestHeadersInterceptor,
        csrfErrorResponseInterceptor,
        noStoreErrorResponseInterceptor,
        xsrfInterceptor,
        // You can add other interceptor functions in this array
      ])
    ),
    provideAnimations(),
    provideTransloco({
      config: { 
        availableLangs: ['en'],
        defaultLang: 'en',
        // Remove this option if your application doesn't support changing language at runtime.
        reRenderOnLangChange: false,
        prodMode: !isDevMode(),
        failedRetries: 2,
      },
      loader: TranslocoHttpLoader
    }),
    provideLottieOptions({
      player: () => player,
    }),
  ]
};
